/**
 * useAdminData — centralised pattern for admin data fetching.
 *
 * Handles: loading, error, data, and one-liner refresh. Replaces the
 * duplicated `useState + useEffect + axios + try/catch/finally` boilerplate
 * that appears in every admin page (and was the root cause of the "48
 * missing hook dependencies" flagged in the code review).
 *
 * Example:
 *   const { data: stats, loading } = useAdminData('/api/admin/stats');
 *   const { data, refresh } = useAdminData('/api/admin/slow-moving');
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function useAdminData(path, { skip = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState(null);

  const fetchIt = useCallback(async () => {
    if (skip || !path) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}${path}`);
      setData(res.data);
    } catch (e) {
      setError(e);
      if (process.env.NODE_ENV === 'development') console.debug('[admin-data]', path, e);
    } finally {
      setLoading(false);
    }
  }, [path, skip]);

  useEffect(() => { fetchIt(); }, [fetchIt]);

  return { data, loading, error, refresh: fetchIt };
}

/**
 * Fetch multiple admin endpoints in parallel. Returns a combined loading
 * flag that is true until ALL are loaded.
 */
export function useAdminDataBatch(paths) {
  // Stabilise the list so deps don't churn.
  const key = paths.join('|');
  const [state, setState] = useState({
    data: {}, loading: true, error: null,
  });

  const fetchAll = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const results = await Promise.all(
        paths.map((p) => axios.get(`${API_URL}${p}`).then((r) => r.data))
      );
      const data = {};
      paths.forEach((p, i) => { data[p] = results[i]; });
      setState({ data, loading: false, error: null });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e }));
      if (process.env.NODE_ENV === 'development') console.debug('[admin-batch]', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return { ...state, refresh: fetchAll };
}
